export enum AssetInventoryType {
  Identities = 'identities',
  Devices = 'devices'
}

/* The path parameter the records/trend endpoints take. */
export enum AssetModel {
  User = 'user',
  Device = 'device'
}

export const ASSET_MODEL: Record<AssetInventoryType, AssetModel> = {
  [AssetInventoryType.Identities]: AssetModel.User,
  [AssetInventoryType.Devices]: AssetModel.Device
}

/* Also the value each user's choice is stored under, so renaming one resets it. */
export enum LastSeenWindow {
  All = 'all',
  SevenDays = '7 days',
  FourteenDays = '14 days',
  ThirtyDays = '30 days',
  SixtyDays = '60 days',
  NinetyDays = '90 days'
}

/* Sent as `last_days` / `last_days_`; null sends no window. The trend only accepts these five. */
export const LAST_SEEN_DAYS: Record<LastSeenWindow, number | null> = {
  [LastSeenWindow.All]: null,
  [LastSeenWindow.SevenDays]: 7,
  [LastSeenWindow.FourteenDays]: 14,
  [LastSeenWindow.ThirtyDays]: 30,
  [LastSeenWindow.SixtyDays]: 60,
  [LastSeenWindow.NinetyDays]: 90
}

/* Sent verbatim as `time_period`; same vocabulary as the ACA trend. */
export enum TrendPeriod {
  OneWeek = '1w',
  OneMonth = '1m',
  ThreeMonths = '3m',
  SixMonths = '6m',
  PastYear = 'past year'
}

export enum AssetColumnKind {
  String = 'string',
  Boolean = 'boolean',
  Array = 'array',
  Timestamp = 'timestamp',
  Number = 'number'
}

/* One column of a model manifest's `spec.columns`; only labelled columns are user-facing. */
export interface AssetManifestColumnWire {
  name: string
  type: string
  displayName?: string | null
  displayByDefault?: boolean
  filterPredicates?: string[] | null
}

export interface AssetModelManifestWire {
  slug: string
  spec: { columns: AssetManifestColumnWire[] }
}

/**
 * A grid column, derived from the model manifest: label, default visibility and kind. Sending a
 * predicate a column does not declare is a 400 that names the accepted set.
 */
export interface AssetColumn {
  name: string
  displayName: string
  kind: AssetColumnKind
  activeByDefault: boolean
  sortable: boolean
  searchable: boolean
}

export interface AssetQueryFilter {
  any_?: string[]
  not_any_?: string[]
  contains_?: string[]
  array_contains_?: string[]
  array_like_?: string[]
  equal_?: boolean
  no_value_?: boolean
  has_value_?: boolean
  gte_?: string | number
  lte_?: string | number
  between_?: [string, string] | [number, number]
  last_days_?: number
}

/* Value lists are capped server-side; exceeding it is a typed QUERY_MODEL_FILTER_LIMIT_ERROR. */
export const MAX_FILTER_VALUES = 50

export type AssetQueryFilters = Record<string, AssetQueryFilter>

export interface AssetSort {
  column: string
  direction: 'ASC' | 'DESC'
}

/**
 * A statistics component's grid shortcut. The predicate is whatever the statistic handed back,
 * forwarded to /records verbatim, so a bar that spans two columns still reads as one chip.
 *
 * The chip prints `label: value` and clears itself by `label`, so the pair is split rather than
 * one string: "OS: Windows", not the whole phrase twice.
 */
export interface AssetFilter {
  id: string
  label: string
  value: string
  queryFilter: AssetQueryFilters
}

export interface AssetRecordsQuery {
  assetType: AssetInventoryType
  /* The tab's columns, which pick the cells a row is mapped from. */
  columns: AssetColumn[]
  lastSeen: LastSeenWindow
  page?: number
  limit?: number
  search?: string
  searchColumns?: string[]
  queryFilter?: AssetQueryFilters
  sort?: AssetSort | null
}

export interface AssetLookupWireResponse {
  available: boolean
  values: string[]
  has_no_value: boolean
  pagination: { page: number; limit: number; has_more: boolean }
}

export interface AssetLookupPage {
  available: boolean
  values: string[]
  hasNoValue: boolean
  hasMore: boolean
}

/* What a column-filter dropdown has loaded so far; `complete` once every page is in. */
export interface AssetLookup {
  available: boolean
  values: string[]
  hasNoValue: boolean
  complete: boolean
}

export const ASSET_SET_FILTER = 'assetSet'

/**
 * A set filter has to record *how* the selection was made, not just what is selected: ticking
 * values by hand sends the values, but Select All over a search term sends the term plus the
 * exclusions, which is a different predicate and has no value-count limit.
 */
export interface AssetSetFilterModel {
  filterType: typeof ASSET_SET_FILTER
  searchTerm: string
  selectAll: boolean
  checked: string[]
  unchecked: string[]
  noValue: boolean
}

/* Sparse, keyed by column: `more` is the +N badge, `value` the array element to print. */
export interface AssetCellMetaEntry {
  value?: string
  more?: number
}

export type AssetCellMeta = Record<string, AssetCellMetaEntry>

export interface AssetRecordWire {
  ENTITY_ID: string
  /* One entry per reporting instance; product slugs on builds before the object form landed. */
  SOURCES: (string | AssetSourceWire)[] | null
  MEMBER_COUNT: number
  CELL_META: AssetCellMeta | null
  [column: string]: unknown
}

export interface AssetRecordsWireMeta {
  last_updated: string | null
  snapshot_date: string | null
}

export interface AssetRecordsWirePagination {
  current_page: number
  next_page: number | null
  previous_page: number | null
  total_results: number
}

export interface AssetRecordsWireResponse {
  available: boolean
  data: AssetRecordWire[]
  meta: AssetRecordsWireMeta | null
  pagination: AssetRecordsWirePagination
}

/* How the merge picked the resolved value for a column. */
export enum AssetAttributeRule {
  Majority = 'majority',
  MostRecent = 'most_recent',
  Earliest = 'earliest',
  AllValues = 'all_values'
}

export interface AssetSourceWire {
  product_name: string
  instance_name: string
  /* Absent where the instance was never labelled, so the name has to be derived instead. */
  instance_display_name?: string
}

export interface AssetAttributeValueWire {
  value: unknown
  sources: AssetSourceWire[]
}

export interface AssetAttributeWire {
  name: string
  rule: AssetAttributeRule
  default_value: unknown
  values: AssetAttributeValueWire[]
}

export interface AssetEntityWire {
  entity_id: string
  member_count: number
  sources: AssetSourceWire[]
  attributes: AssetAttributeWire[]
  members: Record<string, unknown>[]
}

export interface AssetEntityWireResponse {
  available: boolean
  data: AssetEntityWire | null
  meta: AssetRecordsWireMeta | null
}

export interface AssetTrendWirePoint {
  date: string
  count: number
  pointState: 'real' | 'estimated'
}

export interface AssetTrendWireResponse {
  available: boolean
  data: AssetTrendWirePoint[]
}

/**
 * A grid row. `values` holds the sanitized cell text the grid prints, `badges` the count of other
 * distinct values the merged records reported for that column.
 */
export interface AssetRow {
  id: string
  sources: AssetSource[]
  memberCount: number
  values: Record<string, string | null>
  badges: Record<string, number>
}

/**
 * One reporting product instance. Two instances of one product are two sources, and the grid
 * draws a logo per instance, so a device in three Intune tenants shows three icons.
 */
export interface AssetSource {
  product: string
  instance: string
  displayName: string | null
}

/* One distinct value a column took, with the instances that reported it. */
export interface AssetAttributeValue {
  text: string
  sources: AssetSource[]
}

/**
 * A column as the details pane shows it: the value the merge resolved to — the same text the grid
 * cell prints — and every distinct value behind it, the resolved one included.
 */
export interface AssetAttribute {
  column: string
  rule: AssetAttributeRule
  resolved: string | null
  values: AssetAttributeValue[]
}

export interface AssetEntityDetails {
  id: string
  memberCount: number
  sources: AssetSource[]
  attributes: Record<string, AssetAttribute>
  /* The raw records behind the entity, one per reporting instance; the source of origin links. */
  members: Record<string, unknown>[]
}

export interface AssetRecordsResponse {
  available: boolean
  rows: AssetRow[]
  totalResults: number
  hasNextPage: boolean
  lastUpdatedAt: string | null
  snapshotDate: string | null
}

export interface AssetCount {
  total: number
  /* Null where nothing trustworthy sits a month back — a young tenant, or a carried nightly row. */
  changeSinceLastMonth: number | null
}

export interface AssetRecentlyAdded {
  count: number
  days: number
  /* Absent on a tenant with no inventory built: no predicate served, so nothing to click. */
  filter: AssetFilter | null
}

export interface AssetStatisticsWireWindow {
  /* Null for the window that counts every entity. */
  days: number | null
  total: number
  change_since_last_month: number | null
}

export interface AssetStatisticsWireRecentlyAdded {
  count: number
  days: number
  query_filter: AssetQueryFilters
}

export interface AssetStatisticsWireResponse {
  available: boolean
  recently_added: AssetStatisticsWireRecentlyAdded | null
  windows: AssetStatisticsWireWindow[] | null
  meta: AssetRecordsWireMeta | null
}

/**
 * Both statistics cards, for every window at once. The server owns what "created" means, what
 * window counts as recent and how the delta is derived, so moving the Last Seen control is a read
 * out of this rather than a second request.
 */
export interface AssetStatistics {
  available: boolean
  windows: Partial<Record<LastSeenWindow, AssetCount>>
  recentlyAdded: AssetRecentlyAdded
  lastUpdatedAt: string | null
}

export interface AssetTrendPoint {
  date: string
  value: number
  estimated: boolean
}

/* Which of the three kinds of bar this is. Identify the last two by this, never by the label. */
export enum AssetBreakdownKind {
  Value = 'value',
  NoValue = 'no_value',
  Other = 'other'
}

export interface AssetBreakdownWireBucket {
  label: string
  count: number
  kind: AssetBreakdownKind
  query_filter: AssetQueryFilters
}

export interface AssetBreakdownWireResponse {
  available: boolean
  data: AssetBreakdownWireBucket[]
  meta: AssetRecordsWireMeta | null
}

/**
 * One bar. Bars can sum to more than the tab's total — an entity holding two values for the
 * dimension is counted under both — so they are never normalised into a share.
 */
export interface AssetBreakdownBucket {
  label: string
  value: number
  kind: AssetBreakdownKind
  filter: AssetFilter
}

export interface AssetBreakdown {
  available: boolean
  buckets: AssetBreakdownBucket[]
  lastUpdatedAt: string | null
}

export interface AssetBreakdownDimension {
  key: string
  label: string
  column: string
}
